import type { ReactNode } from "react";

import {
  DEFAULT_BUILTIN_TOUR_TABS,
  type ExperienceDto,
} from "@tripcart/shared";

/** One storefront tab as returned by the experience DTO (already ordered and
 *  visibility-filtered by the API — render as-is). */
export type TourTab = NonNullable<ExperienceDto["tabs"]>[number];

/** Built-in tab slugs that resolve to a fixed storefront section, DERIVED from
 *  the shared contract rather than restated here. Custom tabs use their own
 *  slug and resolve against the per-tour custom sections instead.
 *
 *  This was a hand-written copy. Adding a built-in and forgetting it would have
 *  silently dropped that section from the old-API fallback, with nothing red
 *  anywhere — the same drift that made the integration and e2e tab lists reject
 *  every save at once, which is why those are derived too. */
const BUILTIN_TAB_SLUGS: readonly string[] = DEFAULT_BUILTIN_TOUR_TABS.map(
  (builtin) => builtin.slug,
);

/** Section order used when the API sends no tab configuration (legacy
 *  responses / the operator preview, which carries no tabs). The sticky tab
 *  bar is suppressed in that case — this only keeps the sections rendering in a
 *  sensible default sequence so the page never loses content. */
const DEFAULT_BUILTIN_ORDER: readonly TourTab[] = BUILTIN_TAB_SLUGS.map(
  (slug) => ({ slug, label: slug, kind: "builtin" as const }),
);

export interface OrderedSection {
  slug: string;
  node: ReactNode;
}

/**
 * Resolve the storefront body sections in tab order (AC-16/18).
 *
 * Built-in slugs read from `builtinNodes`; custom slugs read from
 * `customNodes`. A tab whose node is absent (slot not supplied — e.g. the
 * preview page omits FAQs) is dropped so it never leaves a gap.
 *
 * An EMPTY `tabs` renders NOTHING when the API sent `visibleTabs`, because that
 * combination is the API saying no section on this tour has content. Only an
 * ABSENT `visibleTabs` — an API too old to answer — falls back to the built-in
 * default order. This block used to say the default order applied whenever
 * `tabs` was empty, which stopped being true on 2026-09-22 and is the one claim
 * a reader is most likely to trust, since it is what an editor shows on hover.
 */
export function orderSections(
  tabs: readonly TourTab[] | undefined,
  builtinNodes: Readonly<Record<string, ReactNode>>,
  customNodes: ReadonlyMap<string, ReactNode>,
  /** From the API: the tabs the operator switched ON, before any
   *  per-experience content filtering.
   *
   *  Read here as one question only — did this API version send the field? Its
   *  CONTENTS are deliberately not rendered; see the `tabs`-empty case below. */
  visibleTabs?: readonly TourTab[],
): OrderedSection[] {
  // `tabs` non-empty        → render it.
  // `tabs` empty, field sent → render NOTHING.
  // field absent (old API)   → the default built-in order.
  //
  // The middle case used to render `visibleTabs` — the operator's ON tabs —
  // reasoning that the API's content filter and this page sometimes disagree,
  // so the nodes we actually have should still show. That was wrong in the one
  // direction that matters: every section slot is a non-null element that
  // self-guards to render nothing, so nothing dropped out, and the page grew a
  // strip of tabs scrolling to sections that painted nothing.
  //
  // An empty `tabs` is the API saying no section on this tour has content —
  // whether because the operator hid them or because they were never filled in
  // is a distinction without a difference HERE, since both answers are "there
  // is no body to show". The hero, booking rail and related tours still render;
  // only the tabbed body is empty, which is the honest result.
  //
  // An ABSENT field still means "an older API cannot tell us", and that keeps
  // the default-order fallback — erring towards showing content rather than
  // emptying a page we have no information about.
  const order: readonly TourTab[] =
    tabs && tabs.length > 0
      ? tabs
      : visibleTabs
        ? []
        : [
            ...DEFAULT_BUILTIN_ORDER,
            ...[...customNodes.keys()].map(
              (slug): TourTab => ({ slug, label: slug, kind: "custom" }),
            ),
          ];

  const result: OrderedSection[] = [];
  for (const tab of order) {
    const node =
      tab.kind === "custom"
        ? customNodes.get(tab.slug)
        : builtinNodes[tab.slug];
    // `null`/`undefined` ⇒ no slot for this slug; drop it. (A slot that
    // self-guards to render nothing is still a non-null element here, which is
    // correct — the API only emits a tab when the tour has content for it.)
    if (node !== null && node !== undefined) {
      result.push({ slug: tab.slug, node });
    }
  }
  return result;
}

/**
 * The built-in slugs whose section heading follows their tab label.
 *
 * TWO of them, and only two: Reviews and Map are what #96/#116 asked for.
 * Renaming any OTHER built-in tab — Overview, Dates & prices, Itinerary,
 * What's included, FAQs — leaves its section heading alone. That is the same
 * symptom, left standing deliberately rather than missed.
 *
 * Note those five are not all alike: Itinerary and What's included already
 * take an operator-set title of their own (`itinerarySectionTitle`,
 * `inclusions.sectionTitle`), so they have a rename path that is simply not
 * the tab. Only the rest are fixed copy.
 *
 * Named rather than spelled at each call site: three pages ask for the same
 * two, and a bare `"reviews"` in a JSX prop also trips
 * `i18next/no-literal-string`, which cannot tell a routing slug from a word on
 * screen.
 */
export const REVIEWS_TAB_SLUG = "reviews";
export const MAPS_TAB_SLUG = "maps";

/**
 * What the operator named one built-in section, from the tab bar.
 *
 * A section's heading and its tab are THE SAME NAME to a traveller: renaming
 * "Reviews" to "What guests say" in Tour Tabs changed the tab and left the
 * section below it still headed "Reviews for this tour". Its two consumers
 * take this as the fallback under any name of their own (the Map's
 * `sectionName`) and above their own built-in English default.
 *
 * `undefined` when the tab is not configured or carries no label — the section
 * then keeps its own translated default, which is also the older-API path.
 */
export function builtinTabLabel(
  tabs: readonly TourTab[] | undefined,
  slug: string,
): string | undefined {
  const label = tabs?.find(
    (tab) => tab.kind !== "custom" && tab.slug === slug,
  )?.label;
  return label && label.trim().length > 0 ? label.trim() : undefined;
}

export interface FaqSplit {
  /** Sections the operator ordered ABOVE the FAQ band. */
  before: OrderedSection[];
  /** The FAQ band, or null when the tour has no FAQ section. */
  faq: OrderedSection | null;
  /** Sections the operator ordered BELOW the FAQ band. */
  after: OrderedSection[];
}

/** The FAQ slug. One place, so the split and the section registry cannot
 *  disagree about which section is the band. */
const FAQ_SLUG = "faqs";

/**
 * Split the body around the FAQ band.
 *
 * FAQs is not an ordinary section: `_faqs-section.tsx` renders a full-bleed
 * tinted band with its own container, which cannot sit inside the two-column
 * grid the rest of the body shares with the booking rail.
 *
 * It used to be lifted out of the ordered list and pinned after everything,
 * which bought that layout at a price the operator paid silently:
 *
 *   - Dragging FAQs in Tour Tabs changed nothing. Its `sort_order` was written
 *     and then ignored.
 *   - Every custom tab rendered above it, even though customs seed at a HIGHER
 *     sort order than `faqs` and the operator's own list showed them below.
 *
 * (Not a symptom: FAQs has no entry in the mobile bottom bar. That bar is
 * curated to four slugs by `BOTTOM_TAB_ICONS` in `_tour-bottom-tabs.client.tsx`
 * and never carried `faqs`, `included` or `maps`.)
 *
 * Splitting around it keeps the band AND the order: `before` renders in the
 * grid, the band spans the page, `after` renders below it. When FAQs is last —
 * the seeded arrangement, and what most tours have — `after` is empty and the
 * page is laid out exactly as it was.
 */
export interface FaqLayout {
  /** True when the band opens the page, with no grid above it. */
  bandLeads: boolean;
  /** The sections that share the grid with the booking rail. */
  main: OrderedSection[];
  /** Sections rendered BELOW the band, in a grid of their own. */
  trailing: OrderedSection[];
  /** The FAQ band, or null when the tour has no FAQ section. */
  faq: OrderedSection | null;
}

/**
 * Where each block goes once the band has taken its place — and, crucially,
 * WHICH block keeps the booking rail.
 *
 * `splitAroundFaq` answers "what is above and below the band". That is not
 * enough on its own: an operator who drags FAQs to position 1 leaves `before`
 * empty, and rendering an empty first block still renders the grid — so the
 * 372px rail sat alone in column two with roughly 600px of blank column one
 * beside it, above the band, next to nothing.
 *
 * With nothing above the band there is nothing to split. The band simply leads
 * the page and everything else is ONE ordinary grid below it, rail beside the
 * departures exactly as usual.
 *
 * The rail therefore follows the main body rather than the first block, which
 * also means an operator cannot strand it by reordering.
 */
export function layoutAroundFaq(body: readonly OrderedSection[]): FaqLayout {
  const { before, faq, after } = splitAroundFaq(body);
  if (faq !== null && before.length === 0) {
    return { bandLeads: true, main: after, trailing: [], faq };
  }
  return { bandLeads: false, main: before, trailing: after, faq };
}

export function splitAroundFaq(body: readonly OrderedSection[]): FaqSplit {
  const faqAt = body.findIndex((entry) => entry.slug === FAQ_SLUG);
  if (faqAt < 0) return { before: [...body], faq: null, after: [] };
  return {
    before: body.slice(0, faqAt),
    faq: body[faqAt]!,
    after: body.slice(faqAt + 1),
  };
}

export interface RailSplit {
  /** Rendered above the booking rail. */
  before: OrderedSection[];
  /** The departures section, which the rail sits beside. Null when this tour
   *  has none. */
  dates: OrderedSection | null;
  /** Rendered below the rail. */
  after: OrderedSection[];
}

/**
 * Split the body around the booking rail.
 *
 * On desktop the rail is its own grid column and this is invisible. On a phone
 * the three wrappers stack in DOM order, so whatever lands in `before` is what
 * a traveller scrolls past before reaching the booking widget.
 *
 * The departures section is the natural anchor — the rail belongs beside it.
 * A tour can legitimately have none (never priced, so the content gate drops
 * the tab; or the operator switched it off), and the fallback is "just after
 * the first section", which keeps the widget near the top.
 *
 * That fallback used to be `body` wholesale, putting the ENTIRE page above the
 * rail: every section, then the booking widget. It was unreachable until
 * `dates` became a droppable tab, at which point it became the long-scroll
 * problem this split exists to prevent.
 */
export function splitBodyForRail(body: readonly OrderedSection[]): RailSplit {
  const datesAt = body.findIndex((entry) => entry.slug === "dates");
  if (datesAt >= 0) {
    return {
      before: body.slice(0, datesAt),
      dates: body[datesAt]!,
      after: body.slice(datesAt + 1),
    };
  }
  const railAt = Math.min(1, body.length);
  return {
    before: body.slice(0, railAt),
    dates: null,
    after: body.slice(railAt),
  };
}

/* `withNonTabSections` and its `NON_TAB_ANCHORS` table lived here until
 * 2026-09-22.
 *
 * The departures list and "What's included" had no tab of their own, so they
 * were spliced into the body afterwards, each anchored "right after" a
 * neighbouring tab and moving with it. That bought them a sensible default
 * position at the cost of three real problems:
 *
 *   - An operator could not rename, reorder or hide either one. Their
 *     position was DERIVED from another tab rather than configured.
 *   - The two tab bars disagreed. The sticky strip renders configured TABS and
 *     so omitted Dates; the mobile bar renders rendered SECTIONS and so
 *     included it. One page, two different answers about what is on it.
 *   - The body slug (`cost`) and the DOM id (`included`) were different words
 *     for one section, so anything navigating by the body slug pointed at a
 *     `#cost` that does not exist.
 *
 * Both are ordinary built-in tabs now (`builtinTourTabKeySchema`), so the
 * operator's order IS the body order and there is nothing left to splice. */
